<template>

    <div class="container">

        <app-head title="Warenkorb optimieren" />

        <div class="my-4">
            <inertia-link :href="route('cart.index')">
                <i class="bi bi-arrow-left"></i>
                {{ __('Zurück zum Warenkorb') }}
            </inertia-link>
        </div>

        <div class="my-4">
            <h1>{{ __('Warenkorb optimieren') }}</h1>
        </div>

        <div class="row row-cols-1 row-cols-lg-3 g-4 mb-4">
            <div class="col">
                <div class="card h-100 border-secondary">
                    <div class="card-header border-0 bg-light fs-4">
                        {{ __('Aktueller Warenkorb') }}
                    </div>
                    <div class="card-body">
                        <div class="h2">
                            {{ originalTotalAmount | localizeAmount }}
                        </div>
                        <hr />
                        <div v-for="group in cartItemsBySupplier" class="border-bottom py-3">
                            <h5 class="m-0">{{ group.supplier.name }}</h5>
                            <div class="small">
                                <div class="mt-2 text-warning" v-if="group.delivery_from == null">
                                    {{ __('Noch keine Versandoptionen konfiguriert.') }}
                                </div>
                                <div class="mt-2 text-success fw-bold" v-else-if="group.delivery_from == 0">
                                    {{ __('Versandkostenfrei') }}
                                </div>
                                <div class="mt-2 fw-bold" v-else>
                                    {{ __('Versand ab :amount', {amount: $options.filters.localizeAmount(group.delivery_from)}) }}
                                </div>
                            </div>
                            <div class="mt-2 d-grid gap-3">
                                <div class="row" v-for="cartItem in group.cart_items">
                                    <div class="col-3">
                                        <img 
                                            class="img-fluid thumbnail"
                                            :src="cartItem.product.main_image_url" 
                                            alt="" 
                                            loading="lazy"
                                            v-if="cartItem.product.main_image_url">
                                    </div>
                                    <div class="col-9">
                                        <h6>{{ cartItem.product_name }}</h6>
                                        <div class="small">
                                            <div>
                                                {{ __('Hersteller-Artikelnr.') }} {{ cartItem.product.manufacturer_pid }}
                                            </div>
                                            <div>
                                                <span>
                                                    {{ __('Einzelpreis: :price', {price: $options.filters.localizeAmount(cartItem.price)}) }}
                                                </span>
                                                &middot;
                                                <span>
                                                    {{ __('Anzahl: :quantity', {quantity: cartItem.quantity}) }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="card-footer p-0">
                        <inertia-link :href="route('cart.index')" class="btn btn-lg btn-light w-100">
                            {{ __('Beibehalten') }}
                        </inertia-link>
                    </div>
                </div>
            </div>
            <div class="col">
                <div class="card h-100 border-success border-4 shadow" style="transform: scale(1.1); z-index: 5;">
                    <div class="card-header border-0 bg-success text-white fs-4">
                        {{ __('Günstigster Gesamtpreis') }}
                    </div>
                    <div class="card-body">
                        <div class="h2 text-success">
                            {{ optimalTotalAmount | localizeAmount }}
                        </div>
                        <div v-if="optimalDiscount > 0">
                            {{ __('Sie sparen:') }}
                            {{ optimalDiscount | localizeAmount }}
                        </div>
                        <hr />
                        <div v-for="group in optimalCartItemsBySupplier" class="border-bottom py-3">
                            <h5 class="m-0">{{ group.supplier.name }}</h5>
                            <div class="small">
                                <div class="mt-2 text-warning" v-if="group.delivery_from == null">
                                    {{ __('Noch keine Versandoptionen konfiguriert.') }}
                                </div>
                                <div class="mt-2 text-success fw-bold" v-else-if="group.delivery_from == 0">
                                    {{ __('Versandkostenfrei') }}
                                </div>
                                <div class="mt-2 fw-bold" v-else>
                                    {{ __('Versand ab :amount', {amount: $options.filters.localizeAmount(group.delivery_from)}) }}
                                </div>
                            </div>
                            <div class="mt-2 d-grid gap-3">
                                <div class="row" v-for="cartItem in group.cart_items">
                                    <div class="col-3">
                                        <img 
                                            class="img-fluid thumbnail"
                                            :src="cartItem.product.main_image_url" 
                                            alt="" 
                                            loading="lazy"
                                            v-if="cartItem.product.main_image_url">
                                    </div>
                                    <div class="col-9">
                                        <h6>{{ cartItem.product_name }}</h6>
                                        <div class="small">
                                            <div>
                                                {{ __('Hersteller-Artikelnr.') }} {{ cartItem.product.manufacturer_pid }}
                                            </div>
                                            <div>
                                                <span>
                                                    {{ __('Einzelpreis: :price', {price: $options.filters.localizeAmount(cartItem.price)}) }}
                                                </span>
                                                &middot;
                                                <span>
                                                    {{ __('Anzahl: :quantity', {quantity: cartItem.quantity}) }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="card-footer p-0">
                        <inertia-link 
                            :href="route('cart.submit-optimize')"
                            :data="optimalCartItemsData"
                            method="post" 
                            as="button"
                            class="btn btn-lg btn-success w-100"
                            >
                            {{ __('Übernehmen') }}
                        </inertia-link>
                    </div>
                </div>
            </div>
            <div class="col">
                <div class="card h-100 border-info">
                    <div class="card-header border-0 bg-info text-white fs-4">
                        {{ __('Favorisierte Lieferanten') }}
                    </div>
                    <div class="card-body">
                        <template v-if="favoriteCartItemsBySupplier.length === 0">
                            <div>
                                {{ __('Noch keine Favoriten') }}
                            </div>
                            <div class="mt-3">
                                <inertia-link :href="route('favorites.index', {origin: 'cart.optimize'})">
                                    {{ __('Favoriten verwalten') }}
                                </inertia-link>
                            </div>
                        </template>
                        <template v-else>
                            <div class="h2">
                                {{ favoriteTotalAmount | localizeAmount }}
                            </div>
                            <div v-if="favoriteDiscount > 0">
                                {{ __('Sie sparen:') }}
                                {{ favoriteDiscount | localizeAmount }}
                            </div>
                            <hr />
                            <div v-for="group in favoriteCartItemsBySupplier" class="border-bottom py-3">
                                <h5 class="m-0">{{ group.supplier.name }}</h5>
                                <div class="small">
                                    <div class="mt-2 text-warning" v-if="group.delivery_from == null">
                                        {{ __('Noch keine Versandoptionen konfiguriert.') }}
                                    </div>
                                    <div class="mt-2 text-success fw-bold" v-else-if="group.delivery_from == 0">
                                        {{ __('Versandkostenfrei') }}
                                    </div>
                                    <div class="mt-2 fw-bold" v-else>
                                        {{ __('Versand ab :amount', {amount: $options.filters.localizeAmount(group.delivery_from)}) }}
                                    </div>
                                </div>
                                <div class="mt-2 d-grid gap-3">
                                    <div class="row" v-for="cartItem in group.cart_items">
                                        <div class="col-3">
                                            <img 
                                                class="img-fluid thumbnail"
                                                :src="cartItem.product.main_image_url" 
                                                alt="" 
                                                loading="lazy"
                                                v-if="cartItem.product.main_image_url">
                                        </div>
                                        <div class="col-9">
                                            <h6>{{ cartItem.product_name }}</h6>
                                            <div class="small">
                                                <div>
                                                    {{ __('Hersteller-Artikelnr.') }} {{ cartItem.product.manufacturer_pid }}
                                                </div>
                                                <div>
                                                    <span>
                                                        {{ __('Einzelpreis: :price', {price: $options.filters.localizeAmount(cartItem.price)}) }}
                                                    </span>
                                                    &middot;
                                                    <span>
                                                        {{ __('Anzahl: :quantity', {quantity: cartItem.quantity}) }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                    <div class="card-footer p-0">
                        <inertia-link 
                            :href="route('cart.submit-optimize')"
                            :data="favoriteCartItemsData"
                            method="post" 
                            as="button"
                            class="btn btn-lg btn-secondary w-100"
                            >
                            {{ __('Übernehmen') }}
                        </inertia-link>
                    </div>
                </div>
            </div>
        </div>

    </div>

</template>

<script>

    export default {
        props: [
            'cartItemsBySupplier',
            'originalTotalAmount',
            'optimalCartItemsBySupplier',
            'optimalTotalAmount',
            'optimalDiscount',
            'optimalCartItemsData',
            'favoriteCartItemsBySupplier',
            'favoriteTotalAmount',
            'favoriteDiscount',
            'favoriteCartItemsData',
        ],
        created() {
//            console.log(this.optimalCartItemsData);
        },
        data() {
            return {
            }
        },
        components: {
//            CartItems,
        },
        watch: {

        },
        methods: {
//            updateCart() {
//                this.$inertia.post(route('cart.update-cart'), this.form, {preserveScroll: false, preserveState: false});
//            }
        },
        computed: {

        },
    }
</script>